import Alpine from 'alpinejs';

import {
  type Card,
  isRed,
  RANK_NAME,
  RANK_PLURAL,
  RANKS,
  type Rank,
  rankValue,
  SUIT_SYMBOL,
} from '../shared/cards.ts';
import {
  type Board,
  type ClientMsg,
  GAME_NAMES,
  type GameId,
  type GinBoard,
  type GoFishBoard,
  type GoFishBookSize,
  type PublicPlayer,
  type RoomView,
  type ServerMsg,
  type WarBoard,
} from '../shared/protocol.ts';

const STORAGE = {
  playerId: 'war.playerId',
  code: 'war.code',
  name: 'war.name',
};

type Status = 'idle' | 'connecting' | 'online' | 'offline';

/** The server serves the client, so same origin is right by default. `?server=` overrides for one-off testing. */
function serverUrl(): string {
  const override = new URLSearchParams(location.search).get('server');

  if (override) {
    const url = new URL(override);
    if (url.protocol === 'http:') url.protocol = 'ws:';
    if (url.protocol === 'https:') url.protocol = 'wss:';
    if (url.pathname === '/' || url.pathname === '') url.pathname = '/ws';
    return url.toString();
  }

  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws`;
}

function read(key: string): string {
  try {
    return localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function write(key: string, value: string): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Private browsing: the session just will not survive a refresh.
  }
}

function shuffled<T>(items: readonly T[]): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const swap = out[i]!;
    out[i] = out[j]!;
    out[j] = swap;
  }
  return out;
}

Alpine.data('table', () => ({
  status: 'idle' as Status,
  screen: 'home' as 'home' | 'room',
  name: read(STORAGE.name),
  joinCode: '',
  error: '',
  view: null as RoomView | null,
  selectedCardId: null as string | null,
  showLog: false,
  copied: false,
  confirmingLeave: false,

  socket: null as WebSocket | null,
  retries: 0,
  errorTimer: 0,
  reconnectTimer: 0,
  leaveTimer: 0,

  init() {
    const invite = new URLSearchParams(location.search).get('table');
    if (invite) this.joinCode = invite.trim().toUpperCase().slice(0, 4);

    const savedId = read(STORAGE.playerId);
    const savedCode = read(STORAGE.code);
    if (savedId && savedCode) {
      this.screen = 'room';
      this.connect(savedCode, savedId);
    }

    addEventListener('online', () => {
      if (this.status === 'offline') this.connect(read(STORAGE.code), read(STORAGE.playerId));
    });

    // iOS suspends sockets when the screen sleeps; wake up the moment we come back.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && this.status === 'offline' && this.screen === 'room') {
        this.connect(read(STORAGE.code), read(STORAGE.playerId));
      }
    });
  },

  // -------------------------------------------------------------- transport

  connect(code: string | null, playerId: string | null) {
    window.clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.status = 'connecting';

    const socket = new WebSocket(serverUrl());
    this.socket = socket;

    socket.onopen = () => {
      this.retries = 0;
      this.status = 'online';
      this.send({
        t: 'hello',
        playerId: playerId || null,
        name: this.name || 'Player',
        code: code || null,
      });
    };

    socket.onmessage = (event) => this.receive(JSON.parse(String(event.data)) as ServerMsg);

    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.status = 'offline';
      if (this.screen !== 'room') return;
      this.retries = Math.min(this.retries + 1, 6);
      this.reconnectTimer = window.setTimeout(
        () => this.connect(read(STORAGE.code), read(STORAGE.playerId)),
        Math.min(400 * 2 ** this.retries, 8000),
      );
    };

    socket.onerror = () => socket.close();
  },

  send(msg: ClientMsg) {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(msg));
  },

  receive(msg: ServerMsg) {
    switch (msg.t) {
      case 'joined':
        write(STORAGE.playerId, msg.playerId);
        write(STORAGE.code, msg.code);
        this.screen = 'room';
        break;
      case 'state':
        this.view = msg.room;
        if (this.selectedCardId && !msg.room.hand.some((card) => card.id === this.selectedCardId)) {
          this.selectedCardId = null;
        }
        break;
      case 'error':
        this.flash(msg.message);
        break;
      case 'kicked':
        this.reset(msg.reason);
        break;
    }
  },

  flash(message: string) {
    this.error = message;
    window.clearTimeout(this.errorTimer);
    this.errorTimer = window.setTimeout(() => (this.error = ''), 3600);
  },

  dismissError() {
    window.clearTimeout(this.errorTimer);
    this.error = '';
  },

  reset(message = '') {
    write(STORAGE.playerId, '');
    write(STORAGE.code, '');
    window.clearTimeout(this.reconnectTimer);
    window.clearTimeout(this.leaveTimer);
    this.confirmingLeave = false;
    this.socket?.close();
    this.socket = null;
    this.view = null;
    this.screen = 'home';
    this.status = 'idle';
    this.selectedCardId = null;
    if (message) this.flash(message);
  },

  // -------------------------------------------------------------- lobby

  createTable() {
    if (!this.name.trim()) return this.flash('Pick a name first.');
    write(STORAGE.name, this.name.trim());
    this.connect(null, null);
  },

  joinTable() {
    if (!this.name.trim()) return this.flash('Pick a name first.');
    const code = this.joinCode.trim().toUpperCase();
    if (code.length !== 4) return this.flash('Table codes are 4 characters.');
    write(STORAGE.name, this.name.trim());
    this.connect(code, null);
  },

  leave() {
    // A hand in progress gets a confirm tap; the lobby, where nothing is at stake, does not.
    if (this.view && this.view.phase !== 'lobby' && !this.confirmingLeave) {
      this.confirmingLeave = true;
      window.clearTimeout(this.leaveTimer);
      this.leaveTimer = window.setTimeout(() => (this.confirmingLeave = false), 3200);
      return;
    }
    window.clearTimeout(this.leaveTimer);
    this.confirmingLeave = false;
    this.send({ t: 'leave' });
    this.reset();
  },

  setGame(game: GameId) {
    this.send({ t: 'setGame', game });
  },

  setGoFishBookSize(value: GoFishBookSize) {
    this.send({ t: 'setGoFishBookSize', value });
  },

  start() {
    this.send({ t: 'start' });
  },

  backToLobby() {
    this.send({ t: 'backToLobby' });
  },

  async copyCode() {
    const code = this.view?.code ?? '';
    const link = `${location.origin}${location.pathname}?table=${code}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Join my card table', text: `Table code ${code}`, url: link });
        return;
      }
      await navigator.clipboard.writeText(link);
      this.copied = true;
      window.setTimeout(() => (this.copied = false), 1800);
    } catch {
      this.flash(`Table code: ${code}`);
    }
  },

  // -------------------------------------------------------------- hand

  get hand(): Card[] {
    return this.view?.hand ?? [];
  },

  reorder(next: Card[]) {
    if (!this.view) return;
    this.view.hand = next;
    this.send({ t: 'reorderHand', order: next.map((card) => card.id) });
  },

  shuffleHand() {
    this.reorder(shuffled(this.hand));
  },

  sortHand() {
    this.reorder(
      this.hand.slice().sort((a, b) => {
        const byRank = rankValue(b.rank) - rankValue(a.rank);
        return byRank !== 0 ? byRank : a.suit.localeCompare(b.suit);
      }),
    );
  },

  tapCard(card: Card) {
    if (!this.canAct) return;
    this.selectedCardId = this.selectedCardId === card.id ? null : card.id;
  },

  get selectedCard(): Card | null {
    return this.hand.find((card) => card.id === this.selectedCardId) ?? null;
  },

  // -------------------------------------------------------------- war

  get war(): WarBoard | null {
    const board: Board | null | undefined = this.view?.board;
    return board?.kind === 'war' ? board : null;
  },

  playTopCard() {
    const card = this.hand[0];
    if (!card) return;
    this.send({ t: 'playCard', cardId: card.id });
  },

  advance() {
    this.send({ t: 'advance' });
  },

  // -------------------------------------------------------------- go fish

  get fish(): GoFishBoard | null {
    const board: Board | null | undefined = this.view?.board;
    return board?.kind === 'gofish' ? board : null;
  },

  get selectedRank(): Rank | null {
    return this.selectedCard?.rank ?? null;
  },

  canAsk(player: PublicPlayer): boolean {
    return this.selectedRank !== null && Boolean(this.fish?.targetIds.includes(player.id));
  },

  ask(player: PublicPlayer) {
    const rank = this.selectedRank;
    if (!rank) return this.flash('Pick one of your cards first.');
    if (!this.fish?.targetIds.includes(player.id)) return this.flash(`${player.name} is out of cards.`);
    this.send({ t: 'ask', targetId: player.id, rank });
    this.selectedCardId = null;
  },

  // -------------------------------------------------------------- gin rummy

  get gin(): GinBoard | null {
    const board: Board | null | undefined = this.view?.board;
    return board?.kind === 'gin' ? board : null;
  },

  ginDraw(from: 'stock' | 'discard') {
    this.send({ t: 'ginDraw', from });
  },

  get canKnockSelected(): boolean {
    return Boolean(this.selectedCardId && this.gin?.legalKnockCardIds.includes(this.selectedCardId));
  },

  get canGinSelected(): boolean {
    return Boolean(this.selectedCardId && this.gin?.legalGinCardIds.includes(this.selectedCardId));
  },

  discardSelected() {
    if (!this.selectedCardId) return;
    this.send({ t: 'ginDiscard', cardId: this.selectedCardId });
    this.selectedCardId = null;
  },

  knockSelected() {
    if (!this.selectedCardId) return;
    this.send({ t: 'ginKnock', cardId: this.selectedCardId });
    this.selectedCardId = null;
  },

  // -------------------------------------------------------------- helpers

  get me(): PublicPlayer | null {
    return this.view?.players.find((player) => player.isYou) ?? null;
  },

  get others(): PublicPlayer[] {
    return this.view?.players.filter((player) => !player.isYou) ?? [];
  },

  nameOf(id: string): string {
    return this.view?.players.find((player) => player.id === id)?.name ?? 'Someone';
  },

  /** True when the table is waiting on this player to move. */
  get canAct(): boolean {
    if (this.view?.phase !== 'playing') return false;
    if (this.war) return this.war.stage === 'choosing' && Boolean(this.me?.active);
    if (this.fish) return this.fish.isYourTurn;
    if (this.gin) return this.gin.isYourTurn;
    return false;
  },

  get gameName(): string {
    return GAME_NAMES[this.view?.game ?? 'war'];
  },

  /** Joined after the current hand was already dealt: not eliminated, just waiting for the next one. */
  get amSpectating(): boolean {
    return this.me?.spectating ?? false;
  },

  get deckHint(): string {
    if (this.canAct) return 'Tap to flip your top card';
    if (this.amSpectating) return "You're watching this hand — you'll join the next one.";
    if (this.me?.done) return "You're out for this hand.";
    return 'Waiting…';
  },

  get handEmptyMessage(): string {
    return this.amSpectating ? "You're watching this hand — you'll join the next one." : 'No cards.';
  },

  get logLines(): string[] {
    return (this.view?.log ?? [])
      .slice()
      .reverse()
      .map((entry) => entry.text);
  },

  suit(card: Card): string {
    return SUIT_SYMBOL[card.suit];
  },

  red(card: Card): boolean {
    return isRed(card.suit);
  },

  rankPlural(rank: Rank): string {
    return RANK_PLURAL[rank];
  },

  rankName(rank: Rank): string {
    return RANK_NAME[rank];
  },

  initials(name: string): string {
    return name.trim().slice(0, 2).toUpperCase();
  },

  /** Books in rank order, so every player's row reads the same way. A rank can repeat in pairs mode. */
  orderedBooks(ranks: Rank[]): Rank[] {
    return ranks.slice().sort((a, b) => RANKS.indexOf(a) - RANKS.indexOf(b));
  },
}));

Alpine.start();
